import { Flex, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { Comic, Release } from "../../../types";
import { ShareButton } from "../../buttons/share/ShareButton";
import { AddToListButton } from "../../lists/AddToListButton";
import { ComicReadingStyleBadge } from "../../cards/components/ComicReadingStyleBadge";
import { GCStaffPickAndExclusiveBadge } from "../../cards/components/GcStaffPickAndExclusiveBadge";
import { GCVerticalsAndOriginalsBadge } from "../../cards/components/GcVerticalsAndOriginalsBadge";
import { EyeIcon } from "../../icons/EyeIcon";
import { CalendarIcon } from "../../icons/CalendarIcon";
import { formatLargeNumber } from "../../../utils/formatLargeNumber";
import { getReleaseTypeName } from "../../../utils/getReleaseTypeName";
import { CollectibleBadge } from "../../cards/components/CollectibleBadge";
import { PagesIcon } from "../../icons/PagesIcon";

export function ReleaseInfoHeader({
  comic,
  release,
}: {
  comic: Comic;
  release: Release;
}) {
  const showCollectibleBadge = !!release?.premium_only && !!!release?.is_free;

  return (
    <Flex flexDirection="column" gap="12px">
      <Flex flexDirection="column">
        <Flex width="100%" justifyContent="space-between" alignItems="center">
          <Text variant="releaseInfoHeaderSuperscript">
            {getReleaseTypeName(release)} #{release.chapter}
          </Text>
          {/*
            Save + share cluster — both are "act on this release without
            committing to read/buy." Add to Lists is labeled (tertiary)
            so the feature stays discoverable while it's rolling out;
            Share stays icon-only (long-established pattern). Popover
            anchors `bottom-end` so it grows leftward into the panel
            instead of off the right edge.
          */}
          <Flex align="center" gap="4px">
            <AddToListButton
              entityType="release"
              entityId={release.id}
              size="sm"
              variant="tertiary"
              placement="bottom-end"
            />
            <ShareButton
              title={t("components.slideInMenu.releaseInfoMenu.share")}
              shareUrl={release.share_url}
              color="dune.dune"
            />
          </Flex>
        </Flex>
        <Text variant="bodyLargeBold">{release.title}</Text>
        <Text variant="bodySmallBold">{comic.artist?.roman_name}</Text>
      </Flex>
      <Flex gap="12px">
        <Flex gap="5px" alignItems="center">
          <CalendarIcon boxSize="16px" color="electric_pink.300" />
          <Text variant="bodyExtraSmallMedium">
            {release.nice_published_time}
          </Text>
        </Flex>
        <Flex gap="5px" alignItems="center">
          <PagesIcon boxSize="16px" color="success.300" />
          <Text variant="bodyExtraSmallMedium">{release.page_count}</Text>
        </Flex>
        <Flex gap="5px" alignItems="center">
          <EyeIcon boxSize="16px" color="blue.300" />
          <Text variant="bodyExtraSmallMedium">
            {formatLargeNumber(release.total_pageviews)}
          </Text>
        </Flex>
      </Flex>
      <Flex gap="8px">
        {showCollectibleBadge && <CollectibleBadge />}
        <GCVerticalsAndOriginalsBadge comicData={comic} isLarge />
        <ComicReadingStyleBadge
          release={release}
          comic={comic}
          fontSize="10px"
          letterSpacing=".25px"
        />
        <GCStaffPickAndExclusiveBadge comicData={comic} isLarge />
      </Flex>
    </Flex>
  );
}
